import { useEffect, useRef } from 'react';

// WS-4b (C6) — global keyboard hook. Spec/12 §12.9.
//
// Truthful scope landed here:
//   • Cmd/Ctrl+K → focus the header global-search input if present;
//                  otherwise navigate to `/search`.
//   • Cmd/Ctrl+/ → toggle the shortcuts modal.
//   • Cmd/Ctrl+, → open the user Profile (spec/12.9).
//   • G then D/C/G/P/A/R/M/I → Vim-style navigation chords (spec/12.9).
//     Two-key state machine with a 1500 ms timeout. The leading `G`
//     arms the chord; the second key fires the navigation. ANY of:
//     focus moving into an editor/form, Escape, a non-chord second key,
//     or the timeout, CANCELS the armed state. The `G` itself is only
//     armed when focus is OUTSIDE an editor/form so the editor's own
//     `g` keypresses (typing the letter) are never intercepted.
//
// Editor safety (non-negotiable): every key in the spec/12.9 catalogue
// that the S1000D editor or DM grids own (Ctrl+S, Ctrl+Z, Ctrl+Y,
// Ctrl+B, Ctrl+I, Alt+Shift+*, F11, single-letter chords, etc.) is
// NOT handled here. The hook also short-circuits when focus is inside
// the editor host (`.ProseMirror`, `[data-editor-host]`) or inside a
// form field — so the user typing the same combinations in the
// editor / a textarea is unaffected. This applies to BOTH the
// modifier shortcuts and the Vim-style chord prefix.
//
// NOT wired in this slice (truthful split):
//   • Esc-to-close-any-dialog as a SINGLE global — today each Dialog
//     mounts its own `useEscapeKey` listener, which is functionally
//     equivalent but not a single shared listener.

export type ChordDestination =
  | 'dashboard'
  | 'csdb'
  | 'graphics'
  | 'projects'
  | 'applicability'
  | 'review'
  | 'manuals'
  | 'import';

interface GlobalShortcutHandlers {
  onOpenSearch(): void;
  onToggleShortcutsModal(): void;
  onOpenProfile(): void;
  onNavigateChord(dest: ChordDestination): void;
}

const FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

// Chord timeout per spec/12.9 intent — long enough to be discoverable,
// short enough that an accidental `G` doesn't linger and swallow the
// next keystroke. 1500 ms matches the catalogue's discoverability
// without crowding normal typing cadence.
const CHORD_TIMEOUT_MS = 1500;

const CHORD_MAP: Record<string, ChordDestination> = {
  d: 'dashboard',
  c: 'csdb',
  g: 'graphics',
  p: 'projects',
  a: 'applicability',
  r: 'review',
  m: 'manuals',
  i: 'import',
};

function isFromEditorOrField(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('.ProseMirror')) return true;
  if (target.closest('[data-editor-host]')) return true;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  const tag = (target as HTMLElement).tagName;
  if (FORM_TAGS.has(tag)) return true;
  return false;
}

export function useGlobalShortcuts(handlers: GlobalShortcutHandlers): void {
  // `chordArmed` and `chordTimer` live in refs so the chord state
  // machine survives across keydown events without re-binding the
  // listener (which would lose the in-flight `G` press).
  const chordArmedRef = useRef<boolean>(false);
  const chordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function clearChord(): void {
      chordArmedRef.current = false;
      if (chordTimerRef.current !== null) {
        clearTimeout(chordTimerRef.current);
        chordTimerRef.current = null;
      }
    }

    function armChord(): void {
      chordArmedRef.current = true;
      if (chordTimerRef.current !== null) {
        clearTimeout(chordTimerRef.current);
      }
      chordTimerRef.current = setTimeout(() => {
        chordArmedRef.current = false;
        chordTimerRef.current = null;
      }, CHORD_TIMEOUT_MS);
    }

    function onKeyDown(ev: KeyboardEvent): void {
      const fromEditor = isFromEditorOrField(ev.target);
      // Editor and form fields: never preempt their key handling.
      // ALSO cancel any in-flight chord — if the user clicked into a
      // textarea after pressing `G`, the chord is no longer intended.
      if (fromEditor) {
        clearChord();
        return;
      }

      // Escape always cancels an armed chord (parallels Vim's
      // <C-c>/<Esc> dismissal). We do NOT preventDefault — other
      // listeners (e.g. dialog close) still get the Escape.
      if (ev.key === 'Escape' && chordArmedRef.current) {
        clearChord();
        return;
      }

      const mod = ev.metaKey || ev.ctrlKey;

      // ── Cmd/Ctrl chord (existing globals) ────────────────────────
      if (mod) {
        // A modifier-combo press also cancels any armed `G` — the
        // user clearly switched intent away from a navigation chord.
        if (chordArmedRef.current) clearChord();
        if (ev.key === 'k' || ev.key === 'K') {
          ev.preventDefault();
          handlers.onOpenSearch();
          return;
        }
        if (ev.key === '/') {
          ev.preventDefault();
          handlers.onToggleShortcutsModal();
          return;
        }
        if (ev.key === ',') {
          ev.preventDefault();
          handlers.onOpenProfile();
          return;
        }
        return;
      }

      // ── Vim-style navigation chord state machine ─────────────────
      // We require `event.key` to be a single ASCII letter and ignore
      // anything with a modifier. The chord is case-insensitive so
      // Shift+G also arms it (matches real Vim ergonomics).
      const key = ev.key;
      if (key.length !== 1) return;
      const lower = key.toLowerCase();

      if (chordArmedRef.current) {
        // We are in the second-key position. Look up the destination.
        const dest = CHORD_MAP[lower];
        // Whatever happens next, the chord state is consumed.
        clearChord();
        if (dest) {
          ev.preventDefault();
          handlers.onNavigateChord(dest);
        }
        return;
      }

      if (lower === 'g') {
        // Arm the chord. We do NOT preventDefault — if the user is
        // typing into a non-form area (rare), the keystroke can still
        // bubble. The chord either resolves on the next keystroke
        // (handled above) or times out.
        armChord();
        return;
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (chordTimerRef.current !== null) {
        clearTimeout(chordTimerRef.current);
        chordTimerRef.current = null;
      }
      chordArmedRef.current = false;
    };
  }, [handlers]);
}
